"use client";

import { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa6";
import { cn } from "@/lib/utils";

export interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  menuClassName?: string;
  ariaLabel?: string;
}

export default function CustomDropdown({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  menuClassName = "",
  ariaLabel = "Dropdown",
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={cn(
        "relative w-full max-w-full",
        isOpen ? "z-50" : "z-10",
        className
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        title={selectedOption ? selectedOption.label : placeholder}
        className="w-full bg-black border border-white rounded-none px-2.5 sm:px-4 py-2 pr-7 sm:pr-8 font-poppins text-xs sm:text-sm text-white text-left flex items-center justify-between focus:outline-none focus:ring-0 focus:border-white cursor-pointer transition-colors select-none"
      >
        <span className="truncate block max-w-full">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FaChevronDown
          className={cn(
            "absolute right-2 sm:right-3.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 sm:w-3 sm:h-3 text-white transition-transform duration-200 pointer-events-none",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute left-0 top-[calc(100%+2px)] w-full min-w-full max-w-full bg-black border border-white rounded-none shadow-2xl z-[100] py-1 max-h-60 overflow-y-auto overflow-x-hidden",
            menuClassName
          )}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                title={opt.label}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={cn(
                  "w-full text-left px-2.5 sm:px-4 py-2 font-poppins text-xs sm:text-sm rounded-none transition-colors truncate block",
                  isSelected
                    ? "bg-white text-black font-medium"
                    : "text-white hover:bg-white/20 hover:text-white"
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

